import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatTimeInTz, formatDateInTz, getActiveTimezone } from '../../lib/dateUtils';
import {
  Search,
  RefreshCw,
  Monitor,
  User,
  Clock,
  AlertTriangle,
  CheckCircle2,
  WifiOff,
  ChevronRight,
  Radio,
  Volume2,
  Maximize2,
  Sparkles,
  Tv,
  Coffee,
  Check,
  X,
  Moon,
  MapPin,
} from 'lucide-react';
import { IosSegmentedControl, SegmentOption } from '../../components/ios/IosSegmentedControl';
import { IosActivityRing } from '../../components/ios/IosActivityRing';
import { IosBottomSheet, WorkstationSheetData } from '../../components/ios/IosBottomSheet';
import { EditDeviceModal } from '../../components/EditDeviceModal';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

interface WorkstationPresence {
  id: string;
  deviceId: string;
  hostname: string;
  deviceName: string;
  os: string;
  agentVersion: string;
  status: 'ACTIVE' | 'INACTIVE' | 'OFFLINE' | string;
  breakStatus?: 'NONE' | 'LUNCH_BREAK' | 'SHORT_BREAK' | 'PENDING_APPROVAL' | string;
  breakExpiresAt?: string | null;
  breakLabel?: string | null;
  breakRequestedType?: 'LUNCH_BREAK' | 'SHORT_BREAK' | string | null;
  breakRequestedDuration?: number;
  lastSeenAt: string;
  lastActivityAt: string | null;
  user: {
    id: string;
    fullName: string;
    email: string;
    department: string | null;
  } | null;
  activeInactivity: {
    id: string;
    startedAt: string;
    elapsedSeconds: number;
  } | null;
}

type FilterStatus = 'ALL' | 'ACTIVE' | 'ON_BREAK' | 'INACTIVE' | 'OFFLINE';

export function LiveMonitorPage() {
  const { user } = useAuth();
  const orgTimezone = getActiveTimezone(user?.organization?.timezone);
  const [searchParams, setSearchParams] = useSearchParams();
  const targetDeviceId = searchParams.get('device');

  const [workstations, setWorkstations] = useState<WorkstationPresence[]>([]);
  const [metrics, setMetrics] = useState({ total: 0, active: 0, inactive: 0, offline: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('ALL');
  const [sseConnected, setSseConnected] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [ringingDeviceId, setRingingDeviceId] = useState<string | null>(null);
  const [ringFeedback, setRingFeedback] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Selected workstation for iOS bottom sheet quick view
  const [sheetWorkstation, setSheetWorkstation] = useState<WorkstationSheetData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any | null>(null);

  // Auto-scroll and prioritize target device from notification
  useEffect(() => {
    if (targetDeviceId && workstations.length > 0) {
      setStatusFilter('ALL');
      haptics.alert();
      sounds.playAlertPing();
      const timer = setTimeout(() => {
        const el = document.getElementById(`workstation-${targetDeviceId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [targetDeviceId, workstations.length]);

  const handleRingDevice = async (deviceId: string, deviceName: string) => {
    try {
      setRingingDeviceId(deviceId);
      setRingFeedback(null);
      haptics.impact();
      sounds.playTapSound();
      const res = await api.admin.ringDevice(deviceId);
      sounds.playChimeSound();
      setRingFeedback(res.message || `Audio alert command dispatched to ${deviceName}!`);
      setTimeout(() => setRingFeedback(null), 6000);
    } catch (err: any) {
      alert(err.message || 'Failed to trigger sound alert');
    } finally {
      setRingingDeviceId(null);
    }
  };

  const handleOpenSheet = (w: WorkstationPresence) => {
    setSheetWorkstation(w);
    setIsSheetOpen(true);
  };

  const handleApprove = async (e: React.MouseEvent, deviceId: string) => {
    e.stopPropagation();
    try {
      setActionLoadingId(deviceId);
      haptics.notificationSuccess();
      sounds.playChimeSound();
      await api.admin.approveBreak(deviceId);
      await fetchSnapshot(true);
    } catch (err: any) {
      alert(err.message || 'Failed to approve break');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDecline = async (e: React.MouseEvent, deviceId: string) => {
    e.stopPropagation();
    try {
      setActionLoadingId(deviceId);
      haptics.impact();
      sounds.playTapSound();
      await api.admin.declineBreak(deviceId);
      await fetchSnapshot(true);
    } catch (err: any) {
      alert(err.message || 'Failed to decline break');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Timer tick for live inactive duration
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchSnapshot = async (isBackground = false) => {
    try {
      if (!isBackground) setError(null);
      const res = await api.admin.getLiveMonitor();
      setWorkstations(res.workstations as WorkstationPresence[]);
      setMetrics(res.metrics);
    } catch (err: any) {
      if (!isBackground) {
        setError(err.message || 'Failed to fetch live workstation snapshot');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSnapshot();

    // Setup SSE connection
    let eventSource: EventSource | null = null;
    try {
      const apiHost = import.meta.env.VITE_API_URL || '';
      eventSource = new EventSource(`${apiHost}/api/v1/admin/realtime`, { withCredentials: true });

      eventSource.onopen = () => {
        setSseConnected(true);
      };

      eventSource.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.type === 'CONNECTED') {
            setSseConnected(true);
            return;
          }

          if (payload.type === 'DEVICE_STATUS_CHANGED') {
            const { deviceId, status } = payload.data;
            if (status === 'INACTIVE') {
              sounds.playAlertPing();
              haptics.alert();
            }
            setWorkstations((prev) =>
              prev.map((w) => {
                if (w.deviceId === deviceId || w.id === deviceId) {
                  return {
                    ...w,
                    status,
                    activeInactivity: status === 'ACTIVE' || status === 'OFFLINE' ? null : w.activeInactivity,
                  };
                }
                return w;
              })
            );
            fetchSnapshot(true);
          } else if (payload.type === 'DEVICE_HEARTBEAT') {
            const { deviceId, status, lastSeenAt, lastActivityAt } = payload.data;
            setWorkstations((prev) =>
              prev.map((w) => {
                if (w.deviceId === deviceId || w.id === deviceId) {
                  return {
                    ...w,
                    status: status || w.status,
                    lastSeenAt: lastSeenAt || new Date().toISOString(),
                    lastActivityAt: lastActivityAt || w.lastActivityAt,
                  };
                }
                return w;
              })
            );
          } else if (payload.type === 'INACTIVITY_EVENT_RESOLVED') {
            fetchSnapshot(true);
          }
        } catch {
          // Ignore unparseable frames (e.g. keep-alives)
        }
      };

      eventSource.onerror = () => {
        setSseConnected(false);
      };
    } catch {
      setSseConnected(false);
    }

    // Edge heartbeat poll every 12s when visible to conserve Cloudflare edge request quota
    const pollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) {
        return; // Skip poll when tab is hidden or backgrounded
      }
      fetchSnapshot(true);
    }, 12000);

    const handleVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        fetchSnapshot(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (eventSource) {
        eventSource.close();
      }
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Format inactive elapsed seconds
  const getElapsedString = (startedAt: string) => {
    const elapsedSecs = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
    const mins = Math.floor(elapsedSecs / 60);
    const secs = elapsedSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getElapsedRawSecs = (startedAt: string) => {
    return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  };

  const getBreakRemainingString = (expiresAt?: string | null) => {
    if (!expiresAt) return null;
    const remainingSecs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
    if (remainingSecs <= 0) return 'Ending now';
    const mins = Math.floor(remainingSecs / 60);
    const secs = remainingSecs % 60;
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  const isWorkstationOnBreak = (w: WorkstationPresence) =>
    Boolean(
      w.breakStatus &&
      w.breakStatus !== 'NONE' &&
      w.status !== 'OFFLINE' &&
      (!w.breakExpiresAt || new Date(w.breakExpiresAt).getTime() > now)
    );

  const onBreakCount = workstations.filter((w) => isWorkstationOnBreak(w)).length;
  const activeCount = workstations.filter((w) => w.status === 'ACTIVE' && !isWorkstationOnBreak(w)).length;
  const inactiveCount = workstations.filter((w) => w.status === 'INACTIVE' && !isWorkstationOnBreak(w)).length;
  const offlineCount = workstations.filter((w) => w.status === 'OFFLINE').length;

  const filteredWorkstations = workstations
    .filter((w) => {
      const matchesSearch =
        search === '' ||
        w.hostname.toLowerCase().includes(search.toLowerCase()) ||
        w.deviceName.toLowerCase().includes(search.toLowerCase()) ||
        (w.user?.fullName && w.user.fullName.toLowerCase().includes(search.toLowerCase())) ||
        (w.user?.email && w.user.email.toLowerCase().includes(search.toLowerCase())) ||
        (w.user?.department && w.user.department.toLowerCase().includes(search.toLowerCase()));

      const matchesStatus =
        statusFilter === 'ALL'
          ? true
          : statusFilter === 'ON_BREAK'
          ? isWorkstationOnBreak(w)
          : statusFilter === 'ACTIVE'
          ? w.status === 'ACTIVE' && !isWorkstationOnBreak(w)
          : statusFilter === 'INACTIVE'
          ? w.status === 'INACTIVE' && !isWorkstationOnBreak(w)
          : w.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (targetDeviceId) {
        if (a.deviceId === targetDeviceId || a.id === targetDeviceId) return -1;
        if (b.deviceId === targetDeviceId || b.id === targetDeviceId) return 1;
      }
      return 0;
    });

  const filterOptions: SegmentOption<FilterStatus>[] = [
    { id: 'ALL', label: 'All', count: workstations.length },
    { id: 'ACTIVE', label: 'Active', count: activeCount, variant: 'emerald' },
    { id: 'ON_BREAK', label: 'On Break', count: onBreakCount, variant: 'blue' },
    { id: 'INACTIVE', label: 'Inactive', count: inactiveCount, variant: 'amber' },
    { id: 'OFFLINE', label: 'Offline', count: offlineCount, variant: 'zinc' },
  ];

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 max-w-7xl mx-auto w-full min-w-0 animate-ios-enter">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Workstation Live Monitor</span>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </h1>
            <div
              className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                sseConnected
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 island-pulse-active'
                  : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
              }`}
            >
              <Radio className="w-3 h-3" />
              <span>{sseConnected ? 'Live Edge Stream' : 'Connecting...'}</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Real-time presence tracking, inactivity alerts, and Windows workstation telemetry
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/floor-plan"
            className="hidden lg:inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 text-xs font-semibold text-violet-600 dark:text-violet-400 border border-violet-500/20 ios-press shadow-sm transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>2D Floor Radar</span>
          </Link>

          <Link
            to="/admin/kiosk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-500/20 ios-press shadow-sm transition-colors"
          >
            <Tv className="w-3.5 h-3.5" />
            <span>TV Kiosk</span>
          </Link>

          <button
            onClick={() => {
              haptics.tap();
              sounds.playTapSound();
              fetchSnapshot(false);
            }}
            className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-zinc-900/80 hover:bg-white dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-800 dark:text-zinc-200 border border-black/[0.08] dark:border-white/10 ios-press shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Sound Alert Dispatched Feedback */}
      {ringFeedback && (
        <div className="p-3.5 rounded-2xl bg-amber-500/15 border border-amber-500/40 text-amber-800 dark:text-amber-200 text-xs flex items-center justify-between shadow-lg shadow-amber-500/10 animate-ios-enter">
          <div className="flex items-center gap-2.5">
            <Volume2 className="w-4 h-4 text-amber-500 animate-pulse" />
            <span className="font-semibold">{ringFeedback}</span>
          </div>
          <button
            onClick={() => setRingFeedback(null)}
            className="text-amber-600 dark:text-amber-400 hover:text-black dark:hover:text-white text-xs font-bold px-2 py-0.5 ios-press"
          >
            ✕
          </button>
        </div>
      )}

      {/* KPI Stats Ribbon - Apple iOS Frosted Widget Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="p-4 rounded-2xl ios-card">
          <div className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Total Enrolled</div>
          <div className="text-2xl font-extrabold text-zinc-900 dark:text-white mt-1 tracking-tight">{workstations.length}</div>
          <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1 font-medium">
            <Monitor className="w-3 h-3" />
            <span>Windows workstations</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-emerald-500/[0.05] dark:bg-emerald-950/20 border-emerald-500/20">
          <div className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Active Now</div>
          <div className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-300 mt-1 tracking-tight">{activeCount}</div>
          <div className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80 mt-1 flex items-center gap-1 font-medium">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span>User active on PC</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-blue-500/[0.06] dark:bg-blue-950/20 border-blue-500/20">
          <div className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">On Break</div>
          <div className="text-2xl font-extrabold text-blue-600 dark:text-blue-300 mt-1 tracking-tight">{onBreakCount}</div>
          <div className="text-[11px] text-blue-600/80 dark:text-blue-400/80 mt-1 flex items-center gap-1 font-medium">
            <Coffee className="w-3 h-3 text-blue-500" />
            <span>Alerts paused</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-amber-500/[0.07] dark:bg-amber-950/20 border-amber-500/30">
          <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Inactive (Alerting)</div>
          <div className="text-2xl font-extrabold text-amber-600 dark:text-amber-300 mt-1 tracking-tight">{inactiveCount}</div>
          <div className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-1 flex items-center gap-1 font-medium">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            <span>Exceeding threshold</span>
          </div>
        </div>

        <div className="p-4 rounded-2xl ios-card bg-zinc-500/[0.05] dark:bg-zinc-900/40 border-black/5 dark:border-zinc-800">
          <div className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Offline</div>
          <div className="text-2xl font-extrabold text-zinc-700 dark:text-zinc-300 mt-1 tracking-tight">{offlineCount}</div>
          <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1 font-medium">
            <WifiOff className="w-3 h-3" />
            <span>Signal &gt;3m ago</span>
          </div>
        </div>
      </div>

      {/* Pending Break Requests Banner */}
      {workstations.some((w) => w.breakStatus === 'PENDING_APPROVAL') && (
        <div className="p-4 rounded-2xl bg-amber-500/10 dark:bg-amber-950/25 border-2 border-amber-500/40 text-amber-900 dark:text-amber-200 shadow-xl shadow-amber-500/5 space-y-3 animate-ios-enter">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
              </div>
              <div>
                <div className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                  <span>Pending Break Approvals</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500 text-white dark:text-black">
                    {workstations.filter((w) => w.breakStatus === 'PENDING_APPROVAL').length} Waiting
                  </span>
                </div>
                <p className="text-[11px] text-zinc-600 dark:text-zinc-400">
                  Workstation users requested break permission. Inactivity alerts are paused awaiting your decision.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {workstations
              .filter((w) => w.breakStatus === 'PENDING_APPROVAL')
              .map((pw) => {
                const isBusy = actionLoadingId === pw.id || actionLoadingId === pw.deviceId;
                const bName = pw.breakLabel || (pw.breakRequestedType === 'LUNCH_BREAK' ? 'Lunch Break' : (pw.breakRequestedType === 'SHORT_BREAK' ? 'Short Break' : (pw.breakRequestedType || 'Break')));
                const bDuration = pw.breakRequestedDuration || (pw.breakRequestedType === 'LUNCH_BREAK' ? 45 : 10);

                return (
                  <div
                    key={pw.id}
                    className="p-3.5 rounded-xl bg-white/90 dark:bg-zinc-900/90 border border-amber-500/30 shadow-sm flex flex-col justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-zinc-900 dark:text-white truncate max-w-[150px]">
                          {pw.deviceName || pw.hostname}
                        </span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold shrink-0">
                          {bDuration} mins
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
                        {pw.user ? pw.user.fullName : 'Assigned User'} • <strong>{bName}</strong>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1 border-t border-amber-500/10">
                      <button
                        disabled={isBusy}
                        onClick={(e) => handleApprove(e, pw.id)}
                        className="flex-1 py-1.5 px-3 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-1.5 transition-colors shadow-sm disabled:opacity-50 ios-press"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Approve</span>
                      </button>
                      <button
                        disabled={isBusy}
                        onClick={(e) => handleDecline(e, pw.id)}
                        className="py-1.5 px-3 rounded-lg text-xs font-semibold bg-zinc-100 dark:bg-zinc-800 hover:bg-rose-500/20 text-zinc-700 dark:text-zinc-300 hover:text-rose-500 flex items-center justify-center gap-1 transition-colors disabled:opacity-50 ios-press"
                      >
                        <X className="w-3.5 h-3.5" />
                        <span>Decline</span>
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Alert Target Focus Banner from Notification */}
      {targetDeviceId && (
        <div className="p-4 rounded-2xl bg-amber-500/15 dark:bg-amber-500/20 border-2 border-amber-500/70 text-amber-900 dark:text-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl shadow-amber-500/10 animate-ios-enter">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-amber-500/25 border border-amber-500/40 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-zinc-900 dark:text-white uppercase tracking-wider">Alert Target Workstation</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-800 dark:text-amber-200 font-mono font-bold">
                  NOTIFIED
                </span>
              </div>
              <p className="text-xs text-amber-800 dark:text-amber-200/90 mt-0.5 font-medium truncate">
                Displaying the workstation that triggered your notification alert.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              haptics.tap();
              setSearchParams({});
            }}
            className="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200 border border-black/10 dark:border-white/10 shrink-0 self-start sm:self-auto ios-press shadow-sm"
          >
            Show All
          </button>
        </div>
      )}

      {/* Filter and Search Bar with iOS Segmented Control */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* iOS Segmented Control */}
        <div className="w-full md:w-auto">
          <IosSegmentedControl
            options={filterOptions}
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            className="w-full md:w-[440px]"
          />
        </div>

        {/* Search input */}
        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3.5 top-2.5" />
          <input
            type="text"
            placeholder="Search employee, host, dept..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3.5 py-2 text-xs bg-white dark:bg-zinc-950/70 border border-black/[0.08] dark:border-white/[0.08] rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-pulse-blue backdrop-blur-xl shadow-inner transition-colors"
          />
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-3.5 bg-rose-500/10 border border-rose-500/25 rounded-2xl text-xs text-rose-500 dark:text-rose-400 animate-ios-enter">
          {error}
        </div>
      )}

      {/* Mobile Workstation Cards Grid (Visible on mobile, hidden on md+) */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 ios-card rounded-2xl">
            <div className="w-5 h-5 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <span className="text-xs">Loading workstations...</span>
          </div>
        ) : filteredWorkstations.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 ios-card rounded-2xl text-xs">
            No workstations match the filter.
          </div>
        ) : (
          filteredWorkstations.map((w) => {
            const isInactive = w.status === 'INACTIVE' && !isWorkstationOnBreak(w);
            const isActive = w.status === 'ACTIVE' && !isWorkstationOnBreak(w);
            const isShiftEnded = w.status === 'SHIFT_ENDED' || w.status === 'DORMANT';
            const isOffline = w.status === 'OFFLINE';
            const isTarget = Boolean(targetDeviceId && (w.deviceId === targetDeviceId || w.id === targetDeviceId));
            const idleStarted = w.activeInactivity?.startedAt || w.lastActivityAt || w.lastSeenAt;
            const elapsed = idleStarted ? getElapsedRawSecs(idleStarted) : 0;

            return (
              <div
                key={w.id}
                id={`workstation-${w.deviceId}`}
                onClick={() => handleOpenSheet(w)}
                className={`p-4 rounded-2xl cursor-pointer ios-card ios-press transition-all ${
                  isTarget
                    ? 'ring-2 ring-amber-400 border-amber-500/70 shadow-xl shadow-amber-500/20'
                    : isInactive
                    ? 'border-amber-500/40 shadow-lg shadow-amber-500/5'
                    : isActive
                    ? 'border-emerald-500/20'
                    : 'border-black/[0.04] dark:border-white/[0.05]'
                }`}
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center border shrink-0 shadow-sm ${
                        isInactive
                          ? 'bg-amber-500/15 border-amber-500/40 text-amber-500'
                          : isActive
                          ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500'
                          : 'bg-black/5 dark:bg-zinc-800/80 border-black/5 dark:border-zinc-700/60 text-zinc-500'
                      }`}
                    >
                      <Monitor className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-zinc-900 dark:text-white text-sm flex items-center gap-1.5 truncate">
                        <span className="truncate">{w.hostname}</span>
                        <span className="text-[10px] px-1.5 py-0.2 rounded-md bg-black/5 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-black/5 dark:border-white/10 shrink-0 font-mono font-semibold">
                          {w.os}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                        {w.user ? w.user.fullName : <span className="italic">Unassigned</span>}
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 flex items-center gap-2">
                    {w.breakStatus === 'PENDING_APPROVAL' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 animate-pulse">
                        <Coffee className="w-3 h-3 text-amber-500" />
                        Approval Pending
                      </span>
                    ) : isWorkstationOnBreak(w) ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
                          <Coffee className="w-3 h-3 text-blue-500" />
                          {w.breakLabel || (w.breakStatus === 'LUNCH_BREAK' ? 'Lunch Break' : 'Short Break')}
                        </span>
                        {w.breakExpiresAt && (
                          <span className="text-[10px] font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                            ⏱ {getBreakRemainingString(w.breakExpiresAt)}
                          </span>
                        )}
                      </div>
                    ) : isActive ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 island-pulse-active">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Active
                      </span>
                    ) : isInactive ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 island-pulse-alert">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        Inactive
                      </span>
                    ) : isShiftEnded ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30">
                        <Moon className="w-3 h-3 text-indigo-500" />
                        Shift Ended
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-black/5 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-black/5 dark:border-zinc-700">
                        Offline
                      </span>
                    )}
                  </div>
                </div>

                {/* Mobile Quick Break Approval Row */}
                {w.breakStatus === 'PENDING_APPROVAL' && (
                  <div className="mt-3 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                      Requested {w.breakRequestedDuration || 10}m break
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={(e) => handleApprove(e, w.id)}
                        disabled={actionLoadingId === w.id}
                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 shadow-sm disabled:opacity-50 ios-press"
                      >
                        <Check className="w-3 h-3" />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={(e) => handleDecline(e, w.id)}
                        disabled={actionLoadingId === w.id}
                        className="px-2 py-1 text-xs font-bold rounded-lg bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:text-rose-500 flex items-center gap-1 disabled:opacity-50 ios-press"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}

                {isInactive && idleStarted && (
                  <div className="mt-3 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IosActivityRing elapsedSeconds={elapsed} thresholdSeconds={120} size={28} />
                      <span className="text-xs text-amber-700 dark:text-amber-300 font-semibold">Idle Duration</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-amber-700 dark:text-amber-300">
                      {getElapsedString(idleStarted)}
                    </span>
                  </div>
                )}

                <div className="mt-3 pt-2.5 border-t border-black/5 dark:border-white/[0.06] flex items-center justify-between gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <span className="text-[11px] font-medium">Tap to inspect & ring</span>
                  <div className="flex items-center gap-1 text-pulse-blue font-bold text-xs">
                    <span>Inspect</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Main Table (Hidden on mobile, visible on md+) */}
      <div className="hidden md:block rounded-2xl ios-card overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
            <thead className="bg-black/[0.03] dark:bg-zinc-900/90 text-zinc-500 dark:text-zinc-400 border-b border-black/[0.06] dark:border-white/[0.08] font-bold">
              <tr>
                <th className="px-4 py-3.5">Workstation & Hostname</th>
                <th className="px-4 py-3.5">Assigned User</th>
                <th className="px-4 py-3.5">Presence State</th>
                <th className="px-4 py-3.5">Inactivity Timer</th>
                <th className="px-4 py-3.5">Last Contact</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.04] dark:divide-white/[0.05]">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-14 text-center text-zinc-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs font-semibold">Streaming workstation telemetry...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredWorkstations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-14 text-center text-zinc-500 font-medium">
                    No workstations match the selected filter.
                  </td>
                </tr>
              ) : (
                filteredWorkstations.map((w) => {
                  const isInactive = w.status === 'INACTIVE' && !isWorkstationOnBreak(w);
                  const isActive = w.status === 'ACTIVE' && !isWorkstationOnBreak(w);
                  const isShiftEnded = w.status === 'SHIFT_ENDED' || w.status === 'DORMANT';
                  const isOffline = w.status === 'OFFLINE';
                  const isTarget = Boolean(targetDeviceId && (w.deviceId === targetDeviceId || w.id === targetDeviceId));
                  const idleStarted = w.activeInactivity?.startedAt || w.lastActivityAt || w.lastSeenAt;
                  const elapsed = idleStarted ? getElapsedRawSecs(idleStarted) : 0;

                  return (
                    <tr
                      key={w.id}
                      id={`workstation-${w.deviceId}`}
                      className={`hover:bg-black/[0.02] dark:hover:bg-zinc-800/40 transition-colors ${
                        isTarget
                          ? 'bg-amber-500/15 border-l-4 border-l-amber-500'
                          : isInactive
                          ? 'bg-amber-500/[0.04]'
                          : ''
                      }`}
                    >
                      {/* Workstation */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center border shadow-sm ${
                              isInactive
                                ? 'bg-amber-500/15 border-amber-500/40 text-amber-500'
                                : isActive
                                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500'
                                : 'bg-black/5 dark:bg-zinc-800/80 border-black/5 dark:border-zinc-700/60 text-zinc-500'
                            }`}
                          >
                            <Monitor className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                              <span>{w.hostname}</span>
                              <span className="text-[10px] px-1.5 py-0.2 rounded-md bg-black/5 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-black/5 dark:border-white/10 font-mono font-semibold">
                                {w.os}
                              </span>
                            </div>
                            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                              {w.deviceId} • v{w.agentVersion}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Assigned User */}
                      <td className="px-4 py-3.5">
                        {w.user ? (
                          <div>
                            <div className="font-bold text-zinc-800 dark:text-zinc-200">{w.user.fullName}</div>
                            <div className="text-[11px] text-zinc-500">{w.user.email}</div>
                            {w.user.department && (
                              <div className="text-[10px] text-pulse-blue font-bold mt-0.5">
                                {w.user.department}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-400 italic">Unassigned</span>
                        )}
                      </td>

                      {/* Current Presence */}
                      <td className="px-4 py-3.5">
                        {w.breakStatus === 'PENDING_APPROVAL' ? (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 animate-pulse">
                            <Coffee className="w-3.5 h-3.5 text-amber-500" />
                            <span>Pending Approval ({w.breakRequestedDuration || 10}m)</span>
                          </div>
                        ) : isWorkstationOnBreak(w) ? (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
                            <Coffee className="w-3.5 h-3.5 text-blue-500" />
                            <span>{w.breakLabel || (w.breakStatus === 'LUNCH_BREAK' ? 'Lunch Break' : 'Short Break')}</span>
                          </div>
                        ) : isActive ? (
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30 island-pulse-active">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span>Active</span>
                          </div>
                        ) : isInactive ? (
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 island-pulse-alert">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Inactive</span>
                          </div>
                        ) : isShiftEnded ? (
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30">
                            <Moon className="w-3.5 h-3.5 text-indigo-500" />
                            <span>Shift Ended</span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium bg-black/5 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-black/5 dark:border-zinc-700">
                            <span className="w-2 h-2 rounded-full bg-zinc-400" />
                            <span>Offline</span>
                          </div>
                        )}
                      </td>

                      {/* Inactivity / Break Elapsed with Activity Ring */}
                      <td className="px-4 py-3.5 font-mono">
                        {isInactive && idleStarted ? (
                          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-300 font-bold text-sm">
                            <IosActivityRing elapsedSeconds={elapsed} thresholdSeconds={120} size={28} />
                            <span>{getElapsedString(idleStarted)}</span>
                          </div>
                        ) : isWorkstationOnBreak(w) && w.breakExpiresAt ? (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-300 border border-blue-500/20 text-xs font-bold">
                            <Coffee className="w-3 h-3 text-blue-500 animate-pulse" />
                            <span>{getBreakRemainingString(w.breakExpiresAt)} left</span>
                          </div>
                        ) : (
                          <span className="text-zinc-400 dark:text-zinc-600">—</span>
                        )}
                      </td>

                      {/* Last Activity */}
                      <td className="px-4 py-3.5 text-[11px] text-zinc-500 dark:text-zinc-400 font-medium">
                        {w.lastActivityAt ? (
                          <div>
                            <div className="font-semibold text-zinc-700 dark:text-zinc-300">{formatTimeInTz(w.lastActivityAt, orgTimezone)}</div>
                            <div className="text-[10px] text-zinc-400">
                              {formatDateInTz(w.lastActivityAt, orgTimezone)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-zinc-400 italic">No activity yet</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {w.breakStatus === 'PENDING_APPROVAL' && (
                            <>
                              <button
                                onClick={(e) => handleApprove(e, w.id)}
                                disabled={actionLoadingId === w.id}
                                title="Approve break request"
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm ios-press disabled:opacity-50"
                              >
                                <Check className="w-3.5 h-3.5" />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={(e) => handleDecline(e, w.id)}
                                disabled={actionLoadingId === w.id}
                                title="Decline break request"
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold bg-zinc-100 dark:bg-zinc-800 hover:bg-rose-500/20 text-zinc-700 dark:text-zinc-300 hover:text-rose-500 ios-press disabled:opacity-50"
                              >
                                <X className="w-3.5 h-3.5" />
                                <span>Decline</span>
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleOpenSheet(w)}
                            title="Open iOS Quick View Sheet"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-zinc-700 dark:text-zinc-300 bg-black/5 dark:bg-zinc-800/80 hover:bg-black/10 dark:hover:bg-zinc-700 border border-black/5 dark:border-white/10 ios-press"
                          >
                            <Maximize2 className="w-3 h-3 text-zinc-500" />
                            <span>Inspect</span>
                          </button>

                          <button
                            onClick={() => handleRingDevice(w.deviceId || w.id, w.deviceName)}
                            disabled={ringingDeviceId === (w.deviceId || w.id)}
                            title="Play remote chime alert on workstation speakers"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/35 ios-press disabled:opacity-50"
                          >
                            <Volume2 className="w-3.5 h-3.5" />
                            <span>{ringingDeviceId === (w.deviceId || w.id) ? 'Ringing...' : 'Ring Alert'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* iOS Bottom Sheet Quick Action Drawer */}
      <IosBottomSheet
        workstation={sheetWorkstation}
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        onRingDevice={handleRingDevice}
        onEditDevice={(w) => {
          setIsSheetOpen(false);
          setEditingDevice(w);
        }}
        isRinging={ringingDeviceId !== null && ringingDeviceId === (sheetWorkstation?.deviceId || sheetWorkstation?.id)}
        now={now}
      />

      <EditDeviceModal
        isOpen={!!editingDevice}
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={() => fetchSnapshot(true)}
      />
    </div>
  );
}
